import React from "react";
import { Space } from "antd";
import { AxCheckbox, AxDivider, AxButton } from "@dynata/axiom3";

const checkboxOptions = ["Apple", "Pear", "Orange"];

class Checkbox extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      checkedList: [],
      indeterminate: false,
      checkAll: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleCheckAllChange = this.handleCheckAllChange.bind(this);
  }
  handleChange(checked) {
    this.setState({ checkedList: checked });
    setTimeout(() => {
      const { checkedList } = this.state;
      this.setState({
        indeterminate:
          !!checkedList.length && checkedList.length < checkboxOptions.length,
        checkAll: checkedList.length === checkboxOptions.length,
      });
    });
  }
  handleCheckAllChange(e) {
    this.setState({
      checkAll: e.target.checked,
      indeterminate: false,
      checkedList: e.target.checked ? checkboxOptions : [],
    });
  }
  render() {
    const { checkedList, indeterminate, checkAll } = this.state;
    return (
      <div className="section" id="checkbox">
        <h3>Checkbox</h3>

        <p>Renders a standard checkbox element.</p>

        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`<AxCheckbox />`}</pre>

          <div className="prop-header">
            <p className="props-title">Props: Checkbox</p>
            <p>
              For AntD props, visit{" "}
              <AxButton
                size="small"
                type="link"
                target="_blank"
                href="https://ant.design/components/checkbox/#API"
              >
                https://ant.design/components/checkbox/#API
              </AxButton>
            </p>
          </div>

          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>checked</td>
                <td>boolean</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>indeterminate</td>
                <td>boolean</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>bordered</td>
                <td>boolean</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>autoFocus</td>
                <td>If get focus when component mounted</td>
                <td>boolean</td>
                <td>false</td>
                <td></td>
              </tr>
              <tr>
                <td>checked</td>
                <td>Specifies whether the checkbox is selected</td>
                <td>boolean</td>
                <td>false</td>
                <td></td>
              </tr>
              <tr>
                <td>defaultChecked</td>
                <td>
                  Specifies the initial state: whether or not the checkbox is
                  selected
                </td>
                <td>boolean</td>
                <td>false</td>
                <td></td>
              </tr>
              <tr>
                <td>disabled</td>
                <td>If disable checkbox</td>
                <td>boolean</td>
                <td>false</td>
                <td></td>
              </tr>
              <tr>
                <td>indeterminate</td>
                <td>The indeterminate checked state of checkbox</td>
                <td>boolean</td>
                <td>false</td>
                <td></td>
              </tr>
              <tr>
                <td>onChange</td>
                <td>
                  The callback function that is triggered when the state changes
                </td>
                <td>function(e:Event)</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>...others</td>
                <td>
                  All extra props are passed down to the{" "}
                  <>
                    <AxButton
                      size="small"
                      type="link"
                      target="_blank"
                      href="https://4x.ant.design/components/checkbox/#API"
                    >
                      {`<Checkbox />`}
                    </AxButton>
                  </>{" "}
                  component.
                </td>
                <td></td>
                <td>-</td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <div className="prop-header">
            <p className="props-title">Props: Checkbox Group</p>
          </div>

          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>defaultValue</td>
                <td>Default selected value</td>
                <td>string[]</td>
                <td>[]</td>
                <td></td>
              </tr>
              <tr>
                <td>disabled</td>
                <td>If disable all checkboxes</td>
                <td>boolean</td>
                <td>false</td>
                <td></td>
              </tr>
              <tr>
                <td>name</td>
                <td>
                  The <code>name</code> property of all{" "}
                  <code>input[type="checkbox"]</code> children
                </td>
                <td>string</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>options</td>
                <td>Specifies options</td>
                <td>string[] | number[] | Option[]</td>
                <td>[]</td>
                <td></td>
              </tr>
              <tr>
                <td>value</td>
                <td>Used for setting the currently selected value</td>
                <td>string[]</td>
                <td>[]</td>
                <td></td>
              </tr>
              <tr>
                <td>onChange</td>
                <td>
                  The callback function that is triggered when the state changes
                </td>
                <td>function(checkedValue)</td>
                <td>-</td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <div className="prop-header">
            <p className="props-title">Method: Checkbox</p>
          </div>

          <table className="props-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>blur()</td>
                <td>Remove focus</td>
                <td></td>
              </tr>
              <tr>
                <td>focus()</td>
                <td>Get focus</td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <AxDivider orientation="left" plain>
            Default
          </AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`
<AxCheckbox />
<AxCheckbox defaultChecked={true} />
<AxCheckbox disabled={true} />
<AxCheckbox checked={true} disabled={true} />
<AxCheckbox>Checkbox</AxCheckbox>
<AxCheckbox bordered={true}>Checkbox</AxCheckbox>
<AxCheckbox defaultChecked={true}>Checkbox</AxCheckbox>
<AxCheckbox disabled={true}>Disabled</AxCheckbox>
<AxCheckbox checked={true} disabled={true}>
  Disabled
</AxCheckbox>`}</pre>
            <Space>
              <AxCheckbox />
              <AxCheckbox defaultChecked={true} />
              <AxCheckbox disabled={true} />
              <AxCheckbox checked={true} disabled={true} />
              <AxCheckbox>Checkbox</AxCheckbox>
              <AxCheckbox bordered={true}>Checkbox</AxCheckbox>
              <AxCheckbox defaultChecked={true}>Checkbox</AxCheckbox>
              <AxCheckbox disabled={true}>Disabled</AxCheckbox>
              <AxCheckbox checked={true} disabled={true}>
                Disabled
              </AxCheckbox>
            </Space>
          </Space>

          <AxDivider orientation="left" plain>
            Indeterminate
          </AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`
  const checkboxOptions = ["Apple", "Pear", "Orange"];
  const [checkedList, setCheckedList] = useState([]);
  const [indeterminate, setIndeterminate] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const handleChange = (checked) => {
    setCheckedList(checked);
  };
  const handleCheckAllChange = (e) => {
    setCheckAll(e.target.checked);
    setIndeterminate(false);
    setCheckedList(e.target.checked ? checkboxOptions : []);
  };
  useEffect(() => {
    setIndeterminate(
      !!checkedList.length && checkedList.length < checkboxOptions.length
    );
    setCheckAll(checkedList.length === checkboxOptions.length);
  }, [checkedList]);
  
  <div className="site-checkbox-all-wrapper">
    <AxCheckbox
      indeterminate={indeterminate}
      onChange={handleCheckAllChange}
      checked={checkAll}
    >
      Check all
    </AxCheckbox>
  </div>
  <AxCheckbox.CheckboxGroup
    options={checkboxOptions}
    value={checkedList}
    onChange={handleChange}
  />
  `}</pre>
            <Space>
              <div className="site-checkbox-all-wrapper">
                <AxCheckbox
                  indeterminate={indeterminate}
                  onChange={this.handleCheckAllChange}
                  checked={checkAll}
                >
                  Check all
                </AxCheckbox>
              </div>
              <AxCheckbox.CheckboxGroup
                options={checkboxOptions}
                value={checkedList}
                onChange={this.handleChange}
              />
            </Space>
          </Space>
        </Space>
      </div>
    );
  }
}
export default Checkbox;
